import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {toast} from 'react-toastify';
import ApiUtils from '../../utils/apiUtils';
import {
  PARTNER_PROFILES,
  PARTNER_SCOPES,
  PARTNER_STATUSES,
} from '../../constants/formFields/partnerFields';
import PartnerCompaniesPanel from './partnerCompaniesPanel';
import {Field, inputSx} from './partnerFormField';

const PartnerDetailDialog = (props) => {
  const [tab, setTab] = useState(0);
  const [partner, setPartner] = useState(null);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);

  const [form, setForm] = useState({
    name: '',
    contactEmail: '',
    description: '',
    status: PARTNER_STATUSES.PENDING.value,
    profile: PARTNER_PROFILES.B.value,
    scopes: [],
    suspendedReason: '',
  });

  const load = async () => {
    const result = await ApiUtils.makeApiCall('GET', `partners/${props.partnerId}`);
    if (!result || result.status === 'error') {
      toast(result?.message || 'Could not load the partner.', {type: 'error'});
      return;
    }
    const data = result.data?.partner;
    setPartner(data);
    setForm({
      name: data?.name ?? '',
      contactEmail: data?.contactEmail ?? '',
      description: data?.description ?? '',
      status: data?.status ?? PARTNER_STATUSES.PENDING.value,
      profile: data?.profile ?? PARTNER_PROFILES.B.value,
      scopes: data?.scopes ?? [],
      suspendedReason: data?.suspendedReason ?? '',
    });
  };

  useEffect(() => {
    if (props.open && props.partnerId) {
      setTab(0);
      load();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open, props.partnerId]);

  const setField = (key, value) => setForm((prev) => ({...prev, [key]: value}));

  const toggleScope = (scope) => {
    setForm((prev) => ({
      ...prev,
      scopes: prev.scopes.includes(scope)
        ? prev.scopes.filter((s) => s !== scope)
        : [...prev.scopes, scope],
    }));
  };

  const save = async () => {
    setSaving(true);
    const result = await ApiUtils.makeApiCall(
      'POST',
      `partners/${props.partnerId}/update`,
      JSON.stringify(form)
    );
    setSaving(false);

    if (!result || result.status === 'error') {
      toast(result?.message || 'Could not save.', {type: 'error'});
      return;
    }

    toast('Partner saved.', {type: 'success'});
    setPartner(result.data?.partner);
    if (props.onChanged) props.onChanged();
  };

  const generateSecret = async () => {
    setGenerating(true);
    const result = await ApiUtils.makeApiCall(
      'POST',
      `partners/${props.partnerId}/generate-secret`
    );
    setGenerating(false);

    if (!result || result.status === 'error') {
      toast(result?.message || 'Could not generate a secret.', {type: 'error'});
      return;
    }

    props.onSecretGenerated({
      partnerName: partner?.name,
      clientId: result.data?.clientId,
      clientSecret: result.data?.clientSecret,
      rotated: result.data?.rotated,
      previousSecretValidUntil: result.data?.previousSecretValidUntil,
    });
    await load();
    if (props.onChanged) props.onChanged();
  };

  const copyClientId = async () => {
    try {
      await navigator.clipboard.writeText(partner?.clientId || '');
      toast('Client ID copied.', {type: 'success'});
    } catch (e) {
      toast('Could not copy.', {type: 'warning'});
    }
  };

  const profileHint = PARTNER_PROFILES[form.profile]?.hint;

  return (
    <Dialog open={props.open} onClose={props.onClose} maxWidth={'md'} fullWidth>
      <DialogTitle>
        {partner?.name || 'Partner'}
        {partner?.status && (
          <Chip
            size={'small'}
            sx={{ml: 2}}
            label={PARTNER_STATUSES[partner.status]?.label || partner.status}
            color={partner.status === 'APPROVED' ? 'success' : 'default'}
          />
        )}
      </DialogTitle>

      <DialogContent dividers>
        <Tabs value={tab} onChange={(e, value) => setTab(value)} sx={{mb: 2}}>
          <Tab label={'Details'}/>
          <Tab label={'Credential'}/>
          <Tab label={`Companies${partner?.grantCount ? ` (${partner.grantCount})` : ''}`}/>
        </Tabs>

        {tab === 0 && (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Field label={'Partner name'} required>
                <TextField
                  value={form.name}
                  onChange={(e) => setField('name', e.target.value)}
                  fullWidth
                  size={'small'}
                  sx={inputSx}
                />
              </Field>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Field label={'Contact email'} required>
                <TextField
                  value={form.contactEmail}
                  onChange={(e) => setField('contactEmail', e.target.value)}
                  fullWidth
                  size={'small'}
                  sx={inputSx}
                />
              </Field>
            </Grid>
            <Grid item xs={12}>
              <Field label={'Purpose'}>
                <TextField
                  value={form.description}
                  onChange={(e) => setField('description', e.target.value)}
                  fullWidth
                  size={'small'}
                  multiline
                  rows={2}
                  sx={inputSx}
                />
              </Field>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Field label={'Status'}>
                <TextField
                  select
                  value={form.status}
                  onChange={(e) => setField('status', e.target.value)}
                  fullWidth
                  size={'small'}
                  sx={inputSx}
                >
                  {Object.values(PARTNER_STATUSES).map((status) => (
                    <MenuItem key={status.value} value={status.value}>
                      {status.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Field>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Field label={'Data profile'}>
                <TextField
                  select
                  value={form.profile}
                  onChange={(e) => setField('profile', e.target.value)}
                  fullWidth
                  size={'small'}
                  sx={inputSx}
                >
                  {Object.values(PARTNER_PROFILES).map((profile) => (
                    <MenuItem key={profile.value} value={profile.value}>
                      {profile.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Field>
            </Grid>

            {profileHint && (
              <Grid item xs={12}>
                <Alert severity={form.profile === 'A' ? 'warning' : 'info'}>
                  {profileHint}
                </Alert>
              </Grid>
            )}

            {form.status === PARTNER_STATUSES.SUSPENDED.value && (
              <Grid item xs={12}>
                <Field label={'Reason for suspension'}>
                  <TextField
                    value={form.suspendedReason}
                    onChange={(e) => setField('suspendedReason', e.target.value)}
                    fullWidth
                    size={'small'}
                    sx={inputSx}
                  />
                </Field>
              </Grid>
            )}

            <Grid item xs={12}>
              <Typography variant={'subtitle2'} sx={{mb: 1}}>Scopes</Typography>
              {Object.values(PARTNER_SCOPES).map((scope) => (
                <FormControlLabel
                  key={scope.value}
                  control={
                    <Checkbox
                      checked={form.scopes.includes(scope.value)}
                      onChange={() => toggleScope(scope.value)}
                    />
                  }
                  label={scope.label}
                />
              ))}
              {Object.values(PARTNER_SCOPES)
                .filter((scope) => scope.warn && form.scopes.includes(scope.value))
                .map((scope) => (
                  <Alert key={scope.value} severity={'warning'} sx={{mt: 1}}>
                    {scope.hint}
                  </Alert>
                ))}
            </Grid>

            <Grid item xs={12}>
              {/*
                Changing the profile, the scopes or the status invalidates every
                token already issued, so the partner cannot keep using the wider
                terms for the rest of the hour.
              */}
              <Alert severity={'info'}>
                Saving a change to status, profile or scopes signs the partner
                out of any tokens they already hold.
              </Alert>
            </Grid>
          </Grid>
        )}

        {tab === 1 && (
          <Box>
            <Box display={'flex'} alignItems={'center'} gap={1} mb={2}>
              <Field label={'Client ID'}>
                <TextField
                  value={partner?.clientId || ''}
                  fullWidth
                  size={'small'}
                  InputProps={{readOnly: true}}
                  sx={inputSx}
                />
              </Field>
              <Tooltip title={'Copy'}>
                <IconButton onClick={copyClientId}><ContentCopyIcon/></IconButton>
              </Tooltip>
            </Box>

            <Divider sx={{my: 2}}/>

            {partner?.hasSecret ? (
              <>
                <Typography variant={'body2'} sx={{mb: 1}}>
                  Current secret ends in <strong>{partner.secretLastFour}</strong>
                  {partner.secretGeneratedAt &&
                    `, generated ${new Date(partner.secretGeneratedAt).toLocaleDateString()}`}.
                </Typography>
                <Alert severity={'warning'} sx={{mb: 2}}>
                  Rotating issues a new secret. The current one keeps working for
                  24 hours so the partner can switch over without downtime.
                </Alert>
                <Button variant={'outlined'} onClick={generateSecret} disabled={generating}>
                  Rotate secret
                </Button>
              </>
            ) : (
              <>
                <Alert severity={'info'} sx={{mb: 2}}>
                  No secret has been generated yet. The partner cannot
                  authenticate until one is.
                </Alert>
                <Button variant={'contained'} onClick={generateSecret} disabled={generating}>
                  Generate secret
                </Button>
              </>
            )}
          </Box>
        )}

        {tab === 2 && props.partnerId && (
          <PartnerCompaniesPanel partnerId={props.partnerId} onChange={() => {
            load();
            if (props.onChanged) props.onChanged();
          }}/>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={props.onClose}>Close</Button>
        {tab === 0 && (
          <Button variant={'contained'} onClick={save} disabled={saving}>
            Save
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

PartnerDetailDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  partnerId: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onChanged: PropTypes.func,
  onSecretGenerated: PropTypes.func.isRequired,
};

PartnerDetailDialog.defaultProps = {
  partnerId: undefined,
  onChanged: undefined,
};

export default PartnerDetailDialog;
